import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';
import BigMailerButton from 'bigmailer/BigMailerButton.component';
import EmailsInfo from 'bigmailer/EmailsInfo.component';
import { Last18MonthsFilter } from 'bigmailer/filters/Last18MonthsFilter.component';
import NextVisitFilter from 'bigmailer/filters/NextVisitFilter.component';
import ServicesFilter from 'bigmailer/filters/ServicesFilter.component';
import StaffFilter from 'bigmailer/filters/StaffFilter.component';
import cn from 'classnames';

import fetchSalon from 'actions/fetchSalon.action';
import ICO_ENVELOP from 'assets/images/icons/envelope-purple.svg';
import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import TypedOption from 'components/form/TypedOption.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Tooltip from 'components/generic/Tooltip.component';
import NEXT_VISIT_FILTER from 'constants/sms/nextVisit.const';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectClientCommsIsFetching } from 'selectors/clientCommunications.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToLowerCase from 'utils/converters/any/fromAnyToLowerCase.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { formatDayToYeaCompact } from 'utils/date/format.util';
import { nowAsDate } from 'utils/date/now.util';
import timeDiffDays from 'utils/date/timeDiffDays.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import { isNotValidId } from 'utils/predicates/fields.util';

import { filterData, getAllClientsList } from 'clientCommunications/actions';

import CN from './ClientEmails.module.scss';

const L = logger('ClientEmails');

const ClientEmails = ({ getAllClientsList, isFetching, filterData }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  const [selectedClients, setSelectedClients] = useState(asArray());

  const [filteredNextVisit, setFilteredNextVisit] = useState(null);
  const [filteredServices, setFilteredServices] = useState(asArray());
  const [filteredStaff, setFilteredStaff] = useState(asArray());

  const [allClients, setAllClients] = useState(asArray());

  const { val: showOnlyWithEmail, tip: toggleShowOnlyWithEmail } = useTip(true);

  const {
    value: clients,
    loading: isLoading,
    error,
  } = useAsync(async () => {
    const hasServices = filteredServices.length > 0;
    const hasStaff = filteredStaff.length > 0;

    if (hasServices || hasStaff) {
      const result = await filterData(
        salonId,
        'list', // for apiSuffix
        filteredStaff, // for stylist Ids
        filteredServices.map((id) => ({ id })), // expect obj
        [], // for data range (not used)
      );
      return result?.json || [];
    } else {
      const result = await getAllClientsList(salonId);
      //  L.unignore('()', 'fetchSalon:', fetchSalon(salonId));
      const all = result?.json || [];
      setAllClients(all); // initial all clients
      return all;
    }
  }, [filterData, filteredServices, getAllClientsList, salonId, filteredStaff]);

  const sortedClients = useMemo(() => {
    if (!clients) return [];

    const eighteenMonthsAgo = nowAsDate().setMonth(nowAsDate().getMonth() - 18);

    const filtered = clients
      .filter((c) => fromAnyToTrimmed(c.user_name) || fromAnyToTrimmed(c.email))
      .filter((c) => (showOnlyWithEmail ? fromAnyToTrimmed(c.email) : true))
      .filter((c) => !fromAnyToBool(c.requested_opt_out))
      .filter((c) => !c.last_visit || new Date(c.last_visit) > eighteenMonthsAgo)
      .filter((c) => {
        if (!filteredNextVisit) return true;
        const nextVisit = c.next_visit ? new Date(c.next_visit) : null;
        const now = nowAsDate();

        if (filteredNextVisit === NEXT_VISIT_FILTER.ANY_DATE) return nextVisit && nextVisit > now;
        if (filteredNextVisit === NEXT_VISIT_FILTER.NEXT_7_DAYS) {
          const diff = timeDiffDays({ bgn: now, end: nextVisit });
          return nextVisit && nextVisit > now && diff <= 7;
        }
        if (filteredNextVisit === NEXT_VISIT_FILTER.NO_FUTURE) return !nextVisit || nextVisit <= now;
        return true;
      });

    return filtered.sort((a, b) => {
      const nameA = fromAnyToLowerCase(asObject(a).user_name);
      const nameB = fromAnyToLowerCase(asObject(b).user_name);
      return nameA.localeCompare(nameB);
    });
  }, [clients, showOnlyWithEmail, filteredNextVisit]);

  const hasSortedClients = sortedClients.length > 0;

  const isAllSelected = useMemo(
    () => hasSortedClients && selectedClients.length === sortedClients.length,
    [hasSortedClients, selectedClients.length, sortedClients.length],
  );

  const visibleSelectedCount = useMemo(
    () => sortedClients.filter((c) => selectedClients.includes(c.id)).length,
    [sortedClients, selectedClients],
  );

  const handleSelectAll = useCallback(() => {
    const allSelected = isAllSelected ? [] : sortedClients.map((c) => c.id);
    setSelectedClients(allSelected);
  }, [isAllSelected, sortedClients]);

  const handleClientSelect = useCallback(
    (clientId) => {
      const newSelected = selectedClients.includes(clientId)
        ? selectedClients.filter((id) => id !== clientId)
        : [...selectedClients, clientId];

      setSelectedClients(newSelected);
    },
    [selectedClients],
  );

  useEffect(() => {
    if (isNotValidId(salonId)) return;
    L.unignore('useEffect()', 'fetchSalon:', fetchSalon(salonId));
  }, [salonId]);

  useEffect(() => {
    setSelectedClients(hasSortedClients ? sortedClients.map((c) => c.id) : asArray());
  }, [hasSortedClients, sortedClients]);

  const hasFilters = fromAnyToBool(filteredNextVisit || filteredServices.length || filteredStaff.length);

  const resetFilters = useCallback(() => {
    setFilteredNextVisit(null);
    setFilteredServices(asArray());
    setFilteredStaff(asArray());
  }, []);

  L.debug('()', clients, { selectedClients, visibleSelectedCount, filteredServices, filteredNextVisit });

  return (
    <div
      data-bem="ClientEmails"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <EmailsInfo />

      <div className={CN.container}>
        <div className={CN.topBar}>
          <div className={CN.pageTitle}>
            <img src={ICO_ENVELOP} alt="" className={CN.titleIcon} />
            {t('clientsEmailPage.topBar.title')} {!isFetching && <BigMailerButton clientsIds={selectedClients} />}
          </div>
          <div className={CN.infoSelection}>
            <div className={CN.clientCount}>
              {t('clientsEmailPage.topBar.numberOfAllClients')}
              <Badge variant="fine">{allClients?.length}</Badge>
            </div>
            <div className={CN.clientCount}>
              {t('clientsEmailPage.topBar.numberOfSelectedClients')}
              <Badge variant="fine"> {visibleSelectedCount}</Badge>
            </div>
          </div>
        </div>
        {/* Filter bar */}
        <div className={CN.filter}>
          <span className={CN.bold}>{t('clientsEmailPage.filtersTitle')}</span>
          <StaffFilter filtered={filteredStaff} onFilter={setFilteredStaff} />
          <ServicesFilter filtered={filteredServices} onFilter={setFilteredServices} />
          <NextVisitFilter filtered={filteredNextVisit} onFilter={setFilteredNextVisit} />
          <Last18MonthsFilter />
          <Button variant="secondary" disabled={!hasFilters} onClick={resetFilters} tag="ClientEmails__reset--filters">
            {t('clientsEmailPage.resetButton')}
          </Button>
        </div>

        <div className={CN.filterBar}>
          <div className={CN.filterBy}>
            <Toggler
              tag="ClientEmails__email"
              onClick={toggleShowOnlyWithEmail}
              selected={showOnlyWithEmail}
              hideLabel
            />
            {t('clientsEmailPage.emailToggle')}
          </div>
          <Tooltip variant="top" width="lg" content={t('clientsEmailPage.optInToggleTooltip')}>
            <div
              className={cn({
                [CN.filterBy]: true,
                [CN.isDisabled]: true,
              })}
            >
              <Toggler tag="ClientEmails__opt-out" selected={true} onClick={alwaysNoop} hideLabel />
              <Icon variant="lockClose" width="sm" /> {t('clientsEmailPage.optInToggle')}
            </div>
          </Tooltip>
        </div>

        {/* Table clients */}
        <div className={CN.panel}>
          {!isLoading && !error && hasSortedClients && (
            <div className={CN.outer} data-bem="ClientEmails__outer">
              <div className={CN.clientsTableWrapper}>
                <div className={CN.clientsTable} data-bem="ClientEmails__table">
                  <div data-bem="ClientEmails__table--header" className={CN.tableHeader}>
                    <div className={CN.colCheckbox}>
                      <TypedOption
                        tag="ClientEmails__client-select-all"
                        type="simple-checkbox"
                        checked={isAllSelected}
                        onCheck={handleSelectAll}
                      />
                    </div>
                    <div className={CN.colName}>{t('clientsEmailPage.clientsTable.nameColumn')}</div>
                    <div className={CN.colEmail}>{t('clientsEmailPage.clientsTable.emailColumn')}</div>
                    <div className={CN.colVisit}>{t('clientsEmailPage.clientsTable.dateColumn')}</div>
                  </div>

                  <div data-bem="ClientEmails__table-body" className={CN.tableBody}>
                    {sortedClients.map((client) => {
                      const { user_name: clientName, email: clientEmail, next_visit: nextVisit } = client || {};
                      return (
                        <div key={client.id} className={CN.tableRow}>
                          <div className={CN.colCheckbox}>
                            <TypedOption
                              tag="ClientEmails__client-select"
                              type="simple-checkbox"
                              checked={selectedClients.includes(client.id)}
                              onCheck={() => handleClientSelect(client.id)}
                            />
                          </div>
                          <div className={CN.colName}>{clientName}</div>
                          <div className={CN.colEmail}>{clientEmail || '---'}</div>
                          <div className={CN.colVisit}>{nextVisit && formatDayToYeaCompact(nextVisit)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isLoading && !error && !hasSortedClients && (
            <div className={CN.noData}>
              <div className={CN.title}>{t('clientsEmailPage.noClients', { suffix: S.faceWithTear })}</div>
            </div>
          )}
          {isLoading && <OverlaySpinner />}
          {error && (
            <div className={CN.noData}>{t('clientsEmailPage.fetchClientsError', { suffix: error.message })}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const ClientEmailsHoc = connect(
  (state) => ({
    isFetching: selectClientCommsIsFetching(state),
  }),
  mapActionsToDispatchToProps({
    getAllClientsList,
    fetchSalon,
    filterData,
  }),
)(ClientEmails);

export default ClientEmailsHoc;
